/**
 * C-01／C-02 鐵粉私訊 CMS helpers (contract v2.2 §1d). Pure — no React / Firebase — so node:test can import them.
 */

/* ---------- errors ---------- */

const DM_ERROR_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "請重新登入後操作",
  FORBIDDEN: "沒有此藝人私訊的操作權限（需藝人本人、小編或工作人員）",
  NOT_FOUND: "找不到資料（可能已被刪除）",
  RATE_LIMITED: "操作太頻繁，請稍後再試",
  MEDIA_NOT_READY: "媒體尚未處理完成或已失效，請重新上傳",
  MEDIA_PROCESSING: "圖片處理中，請稍候幾秒再送出",
  IMAGE_TOO_LARGE: "圖片超過 8MB",
  INVALID_IMAGE: "圖片格式無法辨識",
  CONFLICT: "圖片代號衝突，請重新選擇圖片",
  DM_DISABLED: "私訊尚未開放（遠端開關、試點名單或藝人開關關閉中）",
  DM_CHANNEL_SUSPENDED: "此聊天室已被營運停用，目前可讀不可寫",
  DM_THREAD_NOT_OPEN: "這位粉絲還沒開啟聊天室",
  DM_TEXT_TOO_LONG: "字數超過上限（1,000 字）",
  DM_MEDIA_KIND_UNSUPPORTED: "語音／影片訊息尚未開放",
  DM_SENDER_KIND_FORBIDDEN: "只有藝人本人帳號能以「本人」身分發送；小編與工作人員一律以小編發送",
  INVALID_AUDIO: "語音檔需為 m4a（AAC），且 ≤2MB",
  AUDIO_TOO_LONG: "語音最長 60 秒",
  INVALID_VIDEO: "影片需為 mp4／mov，且 ≤200MB",
  UPLOAD_EXPIRED: "上傳連結已過期，請重新選擇檔案",
  UPLOAD_INTERRUPTED: "上傳中斷，請重新選擇檔案",
  FEATURE_NOT_READY: "此環境尚未開放影片處理",
  TEMPORARILY_UNAVAILABLE: "服務暫時無法使用，請稍後再試",
  DM_BROADCAST_STATE: "這則訊息目前的狀態無法收回",
};
const FORBIDDEN_REASONS: Record<string, string> = {
  artist_only: "只有藝人本人或工作人員能開關私訊",
  staff_only: "只有工作人員能調整規則",
  artist_message: "小編只能收回小編發送的訊息，藝人本人的訊息需由藝人收回",
};
const INVALID_REASONS: Record<string, string> = {
  text: "請檢查文字內容與字數",
  scheduledAt: "排程時間需在 1 分鐘後到 7 天內",
  media: "圖片需 1–4 張，且不可重複",
  poll: "投票選項需 2–4 個、各 1–40 字且不可重複",
  closesAt: "投票截止時間需晚於發送時間，且在 30 天內",
  quietHours: "靜音時段格式錯誤，開始與結束不可相同",
  pausedUntil: "停更結束日需在未來 90 天內",
  pauseNote: "停更公告最多 200 字",
  welcome: "歡迎訊息格式錯誤",
  filter: "篩選條件錯誤",
  month: "月份格式錯誤",
  decision: "此處置不適用於這類檢舉（封鎖只適用粉絲回覆、收回只適用廣播）",
  note: "備註最多 200 字",
  reason: "原因最多 200 字",
  mode: "停用模式錯誤",
  replyId_or_uid: "私人回覆需指定一則回覆",
  senderKind: "發送身分錯誤",
  question: "投票題目字數超過上限",
  blocked: "封鎖參數錯誤",
};
const MEDIA_REASONS: Record<string, string> = { duration: "語音／影片超過 60 秒" };

export function dmErrorMessage(code: string | undefined, reason?: string, status?: number): string {
  if (code === "FORBIDDEN" && reason && FORBIDDEN_REASONS[reason]) return FORBIDDEN_REASONS[reason];
  if (code === "INVALID_ARGUMENT" && reason && INVALID_REASONS[reason]) return INVALID_REASONS[reason];
  if (code === "MEDIA_NOT_READY" && reason && MEDIA_REASONS[reason]) return MEDIA_REASONS[reason];
  if (code === "DM_CHANNEL_SUSPENDED" && reason === "closed") return "此聊天室已關閉，目前可讀不可寫";
  if (code && DM_ERROR_MESSAGES[code]) return DM_ERROR_MESSAGES[code];
  if (status === 404 && !code) return "後端尚未部署鐵粉私訊（B-27～B-30）";
  if (code) return `操作未完成（${code}${reason ? `：${reason}` : ""}）`;
  return "操作未完成，請重試";
}

/* ---------- text ---------- */

/** Contract: lengths are Unicode code points (JS `[...text].length`). */
export const codePoints = (text: string) => [...text].length;
/** `{name}` is stored verbatim; the App substitutes the fan's nickname. */
export const previewName = (text: string, name: string) => text.split("{name}").join(name || "粉絲");

/* ---------- broadcast send ---------- */

export type DmComposeType = "text" | "image" | "voice" | "video" | "poll";
export type DmComposeRole = "artist" | "editor" | "staff";
export type DmComposeSender = "artist" | "team";
export interface DmComposeInput {
  type: DmComposeType;
  text: string;
  assetIds: string[];
  pollOptions: string[];
  /** v2.4: optional poll question shown above the options. */
  pollQuestion?: string;
  /** epoch ms or null */
  pollClosesAt: number | null;
  /** epoch ms or null = send now */
  scheduledAt: number | null;
  /** Caller's team role (dmChannelConfigGet.role); decides which senderKind may be sent. */
  role?: DmComposeRole;
  /** Only honoured for role artist; editor / staff always send "team". */
  senderKind?: DmComposeSender;
}
export interface DmBroadcastSendBody {
  type: DmComposeType;
  text?: string;
  media?: { assetId: string }[];
  poll?: { question?: string; options: string[]; closesAt?: number };
  scheduledAt?: number;
  senderKind?: DmComposeSender;
}
export const DM_TEXT_MAX = 1000;
export const DM_IMAGES_MAX = 4;
export const DM_POLL_OPTION_MAX = 40;
/** Client cap for the optional poll question (backend B-37 is authoritative). */
export const DM_POLL_QUESTION_MAX = 100;
export const DM_PRIVATE_REPLY_MAX = 300;
export const DM_MEDIA_MAX_MS = 60000;
/** Same 500 ms container slack as the backend. */
export const DM_MEDIA_SLACK_MS = 500;
export const DM_VOICE_MAX_BYTES = 2 * 1024 * 1024;
export const DM_VIDEO_MAX_BYTES = 200 * 1024 * 1024;

/** v2.4 sender rule: the artist role may pick 本人／小編; editor and staff are always 小編. */
export function senderKindsFor(role: DmComposeRole | undefined): DmComposeSender[] {
  return role === "artist" ? ["artist", "team"] : ["team"];
}
/** Mirrors dmBroadcastSend: editor / staff asking for "artist" → 403 DM_SENDER_KIND_FORBIDDEN, so refuse it up front. */
export function resolveSenderKind(role: DmComposeRole | undefined, choice: DmComposeSender | undefined): DmComposeSender {
  if (role !== "artist") {
    if (choice === "artist") throw new Error("只有藝人本人帳號能以「本人」身分發送");
    return "team";
  }
  return choice ?? "artist";
}

/** Client-side mirror of dmBroadcastSend validation; throws a Traditional Chinese message. */
export function buildBroadcastBody(input: DmComposeInput, now = Date.now()): DmBroadcastSendBody {
  const text = input.text.trim();
  if (codePoints(text) > DM_TEXT_MAX) throw new Error(`文字最多 ${DM_TEXT_MAX} 字`);
  const body: DmBroadcastSendBody = { type: input.type };
  if (input.role) body.senderKind = resolveSenderKind(input.role, input.senderKind);
  if (input.type === "text") {
    if (!text) throw new Error("請輸入訊息內容");
    body.text = text;
  } else if (input.type === "image") {
    if (input.assetIds.length < 1 || input.assetIds.length > DM_IMAGES_MAX) throw new Error(`圖片需 1–${DM_IMAGES_MAX} 張`);
    if (new Set(input.assetIds).size !== input.assetIds.length) throw new Error("圖片不可重複");
    body.media = input.assetIds.map((assetId) => ({ assetId }));
    if (text) body.text = text;
  } else if (input.type === "voice" || input.type === "video") {
    if (input.assetIds.length !== 1) throw new Error(input.type === "voice" ? "請先上傳 1 則語音" : "請先上傳 1 支影片");
    body.media = [{ assetId: input.assetIds[0] }];
    if (text) body.text = text;
  } else {
    // dmBroadcastSend (v2.3) still requires poll text; the v2.4 question is an extra optional field.
    if (!text) throw new Error("請輸入投票說明文字");
    const options = input.pollOptions.map((o) => o.trim()).filter(Boolean);
    if (options.length < 2 || options.length > 4) throw new Error("投票選項需 2–4 個");
    if (options.some((o) => codePoints(o) > DM_POLL_OPTION_MAX)) throw new Error(`每個選項最多 ${DM_POLL_OPTION_MAX} 字`);
    if (new Set(options).size !== options.length) throw new Error("投票選項不可重複");
    const question = (input.pollQuestion ?? "").trim();
    if (codePoints(question) > DM_POLL_QUESTION_MAX) throw new Error(`投票題目最多 ${DM_POLL_QUESTION_MAX} 字`);
    body.text = text;
    body.poll = { ...(question ? { question } : {}), options };
    if (input.pollClosesAt != null) {
      const base = input.scheduledAt ?? now;
      if (input.pollClosesAt <= base || input.pollClosesAt > base + 30 * 86400000) throw new Error("投票截止時間需晚於發送時間，且在 30 天內");
      body.poll.closesAt = input.pollClosesAt;
    }
  }
  if (input.scheduledAt != null) {
    if (input.scheduledAt < now + 60000 || input.scheduledAt > now + 7 * 86400000) throw new Error("排程時間需在 1 分鐘後到 7 天內");
    body.scheduledAt = input.scheduledAt;
  }
  return body;
}

/* ---------- media files ---------- */

const VOICE_EXT = /\.(m4a|aac)$/i;
/** Pre-upload check for a voice clip (durationMs from the browser's audio metadata; null = unreadable). */
export function voiceFileProblem(file: { name: string; size: number; type: string }, durationMs: number | null): string | null {
  if (!VOICE_EXT.test(file.name) && !/^audio\/(mp4|x-m4a|m4a|aac)$/.test(file.type)) return "語音檔需為 m4a（AAC）";
  if (file.size <= 0 || file.size > DM_VOICE_MAX_BYTES) return "語音檔需 ≤2MB";
  if (durationMs == null) return "無法讀取語音長度，請確認檔案格式";
  if (durationMs > DM_MEDIA_MAX_MS + DM_MEDIA_SLACK_MS) return "語音最長 60 秒";
  return null;
}
/** Pre-upload check for a video clip; returns the mime videoBegin accepts, or a problem. Unknown duration is left to the backend. */
export function videoFileCheck(
  file: { name: string; size: number; type: string },
  durationMs: number | null,
): { mime: "video/mp4" | "video/quicktime" } | { problem: string } {
  const mime =
    file.type === "video/mp4" || file.type === "video/quicktime"
      ? file.type
      : /\.mov$/i.test(file.name)
        ? "video/quicktime"
        : /\.mp4$/i.test(file.name)
          ? "video/mp4"
          : null;
  if (!mime) return { problem: "影片需為 mp4 或 mov" };
  if (file.size < 12 || file.size > DM_VIDEO_MAX_BYTES) return { problem: "影片需 ≤200MB" };
  if (durationMs != null && durationMs > DM_MEDIA_MAX_MS + DM_MEDIA_SLACK_MS) return { problem: "影片最長 60 秒" };
  return { mime };
}
export const formatDuration = (ms: number | null | undefined) => {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/* ---------- broadcast status ---------- */

export const DM_FAIL_REASON_LABELS: Record<string, string> = {
  duration: "影片超過 60 秒",
  media_failed: "影片轉檔失敗",
  timeout: "影片 24 小時內未完成處理",
};
export const failReasonLabel = (reason: string | null | undefined) => (reason ? DM_FAIL_REASON_LABELS[reason] ?? reason : "原因不明");

/* ---------- private reply ---------- */

/** v2.4: private replies are ≤300 code points. Returns the trimmed text or throws. */
export function privateReplyText(text: string): string {
  const t = text.trim();
  if (!t) throw new Error("請輸入回覆內容");
  if (codePoints(t) > DM_PRIVATE_REPLY_MAX) throw new Error(`私人回覆最多 ${DM_PRIVATE_REPLY_MAX} 字`);
  return t;
}

/* ---------- inbox ---------- */

export type DmInboxFilter = "unseen" | "hearted" | "reported" | "hidden" | "blocked" | "months3" | "months6" | "months12";
export const DM_INBOX_FILTERS: { value: DmInboxFilter | ""; label: string }[] = [
  { value: "", label: "全部" },
  { value: "unseen", label: "未看" },
  { value: "months3", label: "鐵粉 ≥3 個月" },
  { value: "months6", label: "≥6 個月" },
  { value: "months12", label: "≥12 個月" },
  { value: "hearted", label: "有愛心" },
  { value: "reported", label: "已檢舉" },
  { value: "hidden", label: "已隱藏" },
  { value: "blocked", label: "已封鎖" },
];
/** v2.4: broadcastId and filter combine on the backend (filter inside one broadcast's group). Empty values are omitted. */
export function inboxQuery(opts: { broadcastId?: string | null; filter?: DmInboxFilter | "" | null }): { broadcastId?: string; filter?: DmInboxFilter } {
  return { ...(opts.broadcastId ? { broadcastId: opts.broadcastId } : {}), ...(opts.filter ? { filter: opts.filter } : {}) };
}
/** Groups replies by broadcast, keeping first-seen order (the API returns newest first). */
export function groupByBroadcast<T extends { broadcastId: string | null }>(items: T[]): { broadcastId: string | null; items: T[] }[] {
  const groups = new Map<string, { broadcastId: string | null; items: T[] }>();
  for (const item of items) {
    const key = item.broadcastId ?? "";
    const g = groups.get(key) ?? { broadcastId: item.broadcastId, items: [] };
    g.items.push(item);
    groups.set(key, g);
  }
  return [...groups.values()];
}

/* ---------- insights ---------- */

/** dmInsights.subscriptionDays bucket order (B-33 SUBSCRIPTION_DAY_BUCKETS). */
export const SUBSCRIPTION_DAY_BUCKETS = ["<30", "30-89", "90-179", "180-364", "365+"] as const;
const BUCKET_LABELS: Record<string, string> = {
  "<30": "未滿 30 天",
  "30-89": "30–89 天",
  "90-179": "90–179 天",
  "180-364": "180–364 天",
  "365+": "365 天以上",
};
export interface SubscriptionDayBar { key: string; label: string; count: number; share: number; width: number }
/** Bars in bucket order; share = count / total, width = count / largest bucket (0–1). Unknown keys are appended. */
export function subscriptionDayBars(buckets: Record<string, number> | null | undefined): SubscriptionDayBar[] {
  const src = buckets ?? {};
  const known = SUBSCRIPTION_DAY_BUCKETS as readonly string[];
  const keys = [...known, ...Object.keys(src).filter((k) => !known.includes(k))];
  const counts = keys.map((k) => (typeof src[k] === "number" && src[k] > 0 ? src[k] : 0));
  const total = counts.reduce((a, b) => a + b, 0);
  const top = Math.max(0, ...counts);
  return keys.map((key, i) => ({
    key,
    label: BUCKET_LABELS[key] ?? key,
    count: counts[i],
    share: total ? counts[i] / total : 0,
    width: top ? counts[i] / top : 0,
  }));
}

/* ---------- refund claims ---------- */

export type DmRefundClaimStatus = "received" | "refunding" | "apple_guided" | "refunded";
export const DM_REFUND_STATUS_LABELS: Record<string, string> = {
  received: "待處理",
  refunding: "退款處理中",
  apple_guided: "已引導 Apple 退款",
  refunded: "已退款",
};
export const DM_REFUND_PLATFORM_LABELS: Record<string, string> = { ios: "iOS（Apple）", android: "Android（Google Play）", coin: "金幣" };
const DM_REFUND_ERROR_LABELS: Record<string, string> = {
  NO_ORDER_ID: "找不到 Google Play 訂單編號，需人工退款",
  PLAY_CLIENT_UNAVAILABLE: "Google Play 退款服務未設定",
  ALREADY_REFUNDED: "已退過款",
  NO_PRICE: "找不到本期扣款金額",
  NO_USER: "找不到使用者",
};
export function refundErrorLabel(code: string | null | undefined): string {
  if (!code) return "";
  if (code.startsWith("PLAY_REFUND_FAILED")) return `Google Play 退款失敗${code.includes(":") ? `（${code.split(":")[1]}）` : ""}`;
  return DM_REFUND_ERROR_LABELS[code] ?? code;
}

/* ---------- moderation ---------- */

export type DmModerationDecision = "hide" | "dismiss" | "block_fan" | "retract_broadcast";
export type DmReportTarget = "reply" | "message" | "broadcast";
export const DM_DECISION_LABELS: Record<DmModerationDecision, string> = {
  dismiss: "保留（不處置）",
  hide: "隱藏",
  block_fan: "封鎖粉絲（此藝人私訊）",
  retract_broadcast: "收回廣播",
};
/** Contract: block_fan only for target reply; retract_broadcast only for target broadcast. */
export function decisionsFor(target: string | null | undefined): DmModerationDecision[] {
  if (target === "reply") return ["dismiss", "hide", "block_fan"];
  if (target === "broadcast") return ["dismiss", "hide", "retract_broadcast"];
  return ["dismiss", "hide"];
}
/** Suspending a channel requires typing the artist's name exactly (ignoring surrounding spaces). */
export const suspendConfirmed = (typed: string, artistName: string) => !!artistName.trim() && typed.trim() === artistName.trim();

/* ---------- months ---------- */

/** "YYYY-MM" in Asia/Taipei (UTC+8, no DST). */
export function taipeiMonth(ms: number): string {
  const d = new Date(ms + 8 * 3600000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
/** Current Taipei month and the previous n−1 months, newest first. */
export function recentMonths(n: number, now = Date.now()): string[] {
  const [y, m] = taipeiMonth(now).split("-").map(Number);
  return Array.from({ length: n }, (_, i) => {
    const total = y * 12 + (m - 1) - i;
    return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
  });
}
